// T5-TASK-SEARCH: the task search screen's filters as one value, and the `POST tasks/search` body
// they make — the same filters the MCP `search_tasks` takes (boards, statuses, assignees, tags,
// a due-date range, field conditions with ranges), so a person and a DE find the same tasks.
import type { ViewCondition } from "./view-engine";

export type FieldCond = {
  /** The field's id (`field:{id}` in the filter). */
  fieldId: string;
  /** Shown on the chip. */
  label: string;
} & (
  | { kind: "range"; min?: string; max?: string; date: boolean }
  | { kind: "in"; values: string[]; names: string[] }
  | { kind: "contains"; text: string }
  | { kind: "bool"; value: boolean }
);

export type SearchState = {
  q: string;
  boards: string[];
  statuses: string[];
  assignees: string[];
  tags: string[];
  due: { from?: string; to?: string };
  fields: FieldCond[];
  includeDone: boolean;
};

export const EMPTY_SEARCH: SearchState = {
  q: "",
  boards: [],
  statuses: [],
  assignees: [],
  tags: [],
  due: {},
  fields: [],
  includeDone: true,
};

export const SEARCH_INCLUDE = ["brief", "numbers", "links", "last_status_change"] as const;

/** `YYYY-MM-DD` → the next day's midnight UTC (a date `to` covers that whole day). */
export function dayAfter(day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString();
}

const dayStart = (day: string) => new Date(`${day}T00:00:00Z`).toISOString();

function fieldConditions(c: FieldCond): ViewCondition[] {
  const field = `field:${c.fieldId}`;
  switch (c.kind) {
    case "range": {
      const out: ViewCondition[] = [];
      if (c.min) out.push({ field, op: "gte", value: c.date ? dayStart(c.min) : Number(c.min) });
      if (c.max)
        out.push(
          c.date
            ? { field, op: "lt", value: dayAfter(c.max) }
            : { field, op: "lte", value: Number(c.max) },
        );
      return out;
    }
    case "in":
      return c.values.length ? [{ field, op: "in", value: c.values }] : [];
    case "contains":
      return c.text.trim() ? [{ field, op: "contains", value: c.text.trim() }] : [];
    case "bool":
      return [{ field, op: "eq", value: c.value }];
  }
}

/** Whether any filter beyond the text is on. */
export function hasFilters(s: SearchState): boolean {
  return (
    s.boards.length > 0 ||
    s.statuses.length > 0 ||
    s.assignees.length > 0 ||
    s.tags.length > 0 ||
    Boolean(s.due.from || s.due.to) ||
    s.fields.length > 0 ||
    !s.includeDone
  );
}

/** The request body for a search state (`limit` per page). */
export function searchBody(s: SearchState, limit = 50) {
  const all: ViewCondition[] = [];
  if (s.statuses.length) all.push({ field: "status", op: "in", value: s.statuses });
  if (s.assignees.length) all.push({ field: "assignee", op: "in", value: s.assignees });
  if (s.tags.length) all.push({ field: "tag", op: "in", value: s.tags });
  if (s.due.from) all.push({ field: "due_at", op: "gte", value: dayStart(s.due.from) });
  if (s.due.to) all.push({ field: "due_at", op: "lt", value: dayAfter(s.due.to) });
  for (const c of s.fields) all.push(...fieldConditions(c));
  const filters = {
    ...(all.length ? { all } : {}),
    ...(s.includeDone ? {} : { include_done: false }),
  };
  const q = s.q.trim();
  return {
    ...(q ? { query: q } : {}),
    ...(s.boards.length ? { board_ids: s.boards } : {}),
    ...(Object.keys(filters).length ? { filters } : {}),
    include: [...SEARCH_INCLUDE],
    limit,
  };
}

/** A field condition's chip text ("Score 10–50", "Stage: Won, Lost", "Notes has “x”"). */
export function fieldChip(c: FieldCond): string {
  switch (c.kind) {
    case "range":
      if (c.min && c.max) return `${c.label} ${c.min}–${c.max}`;
      return c.min ? `${c.label} ≥ ${c.min}` : `${c.label} ≤ ${c.max ?? ""}`;
    case "in":
      return `${c.label}: ${c.names.join(", ")}`;
    case "contains":
      return `${c.label} has “${c.text}”`;
    case "bool":
      return `${c.label}: ${c.value ? "yes" : "no"}`;
  }
}

/** The due chip's text. */
export function dueChip(due: SearchState["due"]): string | null {
  if (due.from && due.to) return `Due ${due.from} – ${due.to}`;
  if (due.from) return `Due from ${due.from}`;
  if (due.to) return `Due by ${due.to}`;
  return null;
}

/** The kind of condition a field type takes on this screen (null: not offered). */
export function condKindOf(type: string): "number" | "date" | "choice" | "text" | "bool" | null {
  switch (type) {
    case "number":
    case "currency":
      return "number";
    case "date":
      return "date";
    case "select":
    case "multi_select":
      return "choice";
    case "text":
    case "url":
      return "text";
    case "checkbox":
      return "bool";
    default:
      return null;
  }
}
